import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGenerateInvite } from '@/hooks/useGroupInvites';
import { useGroupItems } from '@/hooks/useItems';
import { useAuth } from '@/contexts/AuthContext';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import { getAppUrl } from '@/lib/constants';


interface InvitePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
}

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onOpenChange
 * @param root0.groupId
 */
export function InvitePanel({ open, onOpenChange, groupId }: InvitePanelProps) {
  const { user } = useAuth();
  const generateInvite = useGenerateInvite();
  const { data: itemsData } = useGroupItems(groupId);
  const items = useMemo(() => itemsData?.pages.flatMap((p) => p.items), [itemsData]);

  const [selectedItemId, setSelectedItemId] = useState<string>('none');
  const [inviteLink, setInviteLink] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(false);

  const availableItems = useMemo(() => {
    if (!items || !user) return [];
    return items.filter((i) => i.owner_id === user.id && !i.active_loan);
  }, [items, user]);

  useEffect(() => {
    if (!open) return;
    setGenerating(true);
    setCopied(false);
    setGenerateError(false);

    const itemId = selectedItemId !== 'none' ? selectedItemId : undefined;
    generateInvite
      .mutateAsync({ groupId, itemId })
      .then((invite) => {
        const code = (invite as any).invite_code;
        const link = `${getAppUrl()}/invite/${code}${itemId ? `?item=${itemId}` : ''}`;
        setInviteLink(link);

        if (itemId) {
          const itemName = availableItems.find((i) => i.id === itemId)?.name || 'an item';
          setMessage(
            `I've got a ${itemName} available if you need one. Join my group on Share Shack: ${link}`,
          );
        } else {
          setMessage(
            `I'm setting up a group to keep track of stuff we lend each other. Join here: ${link}`,
          );
        }
        setGenerating(false);
      })
      .catch(() => {
        setGenerating(false);
        setGenerateError(true);
      });
  }, [open, selectedItemId, groupId]);

  const handleCopy = async () => {
    await copyToClipboard(message);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen) {
      setSelectedItemId('none');
      setInviteLink('');
      setMessage('');
      setCopied(false);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite to Group</DialogTitle>
          <DialogDescription>Share this message to invite someone to your group.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Item attachment (optional) */}
          {availableItems.length > 0 && (
            <div className="space-y-2">
              <Label>Attach an item (optional)</Label>
              <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                <SelectTrigger>
                  <SelectValue placeholder="No item — general invite" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No item — general invite</SelectItem>
                  {availableItems.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Editable message */}
          <div className="space-y-2">
            <Label>Message</Label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              disabled={generating}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: `1.5px solid ${T.border}`,
                borderRadius: RADIUS.md,
                background: T.surfaceSubtle,
                color: T.text,
                fontSize: 14,
                fontFamily: 'inherit',
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                lineHeight: 1.55,
              }}
              placeholder={generating ? 'Generating invite link…' : ''}
            />
          </div>

          {/* Error state */}
          {generateError && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <p style={{ fontSize: 13, color: T.danger, marginBottom: 8 }}>
                Failed to generate invite link. Please try again.
              </p>
              <button
                type="button"
                onClick={() => {
                  setGenerateError(false);
                  setGenerating(true);
                  const retryItemId = selectedItemId !== 'none' ? selectedItemId : undefined;
                  generateInvite
                    .mutateAsync({ groupId, itemId: retryItemId })
                    .then((invite) => {
                      const code = (invite as any).invite_code;
                      const link = `${getAppUrl()}/invite/${code}${retryItemId ? `?item=${retryItemId}` : ''}`;
                      setInviteLink(link);
                      if (retryItemId) {
                        const retryItemName = availableItems.find((i) => i.id === retryItemId)?.name || 'an item';
                        setMessage(
                          `I've got a ${retryItemName} available if you need one. Join my group on Share Shack: ${link}`,
                        );
                      } else {
                        setMessage(
                          `I'm setting up a group to keep track of stuff we lend each other. Join here: ${link}`,
                        );
                      }
                      setGenerating(false);
                    })
                    .catch(() => {
                      setGenerating(false);
                      setGenerateError(true);
                    });
                }}
                style={{
                  padding: '8px 16px',
                  borderRadius: RADIUS.sm,
                  background: T.surfaceSubtle,
                  color: T.textSec,
                  border: `1px solid ${T.border}`,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* Copy CTA */}
          <button
            onClick={handleCopy}
            disabled={!inviteLink || generating}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: RADIUS.md,
              background: !inviteLink || generating ? T.surfaceSubtle : T.accent,
              color: !inviteLink || generating ? T.textMuted : T.white,
              border: !inviteLink || generating ? `1px solid ${T.border}` : 'none',
              fontSize: 15,
              fontWeight: 600,
              cursor: !inviteLink || generating ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: !inviteLink || generating ? 'none' : SHADOW.btn.accent,
            }}
          >
            {generating ? 'Generating…' : copied ? '✓ Copied' : 'Copy Invite Link'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
